// ✅ OutilDetail.jsx – Détail d'un outil agricole avec navigation médias (image/vidéo) + suggestions similaires
import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { db } from "../firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import MenuNav from "../components/Menunav";
import MobileMenu from "../components/MobileMenu";
import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import FooterOutils from "../components/Footer";
import { Helmet } from "react-helmet-async";

export default function OutilDetail() {
  const { slug } = useParams();
  const [outil, setOutil] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔹 Toujours remonter en haut de la page quand on arrive sur un outil
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth", // tu peux mettre "auto" si tu ne veux pas l’animation
    });
  }, [slug]);
    // 🔹 Toujours remonter en haut de la page quand on arrive/changede d’outil
  useLayoutEffect(() => {
    // pour la plupart des navigateurs
    window.scrollTo(0, 0);

    // sécurité : on force aussi sur <html> et <body>
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [slug]);


  // Médias
  const [mediaList, setMediaList] = useState([]); // images + vidéo
  const [imageList, setImageList] = useState([]); // uniquement images (lightbox)
  const [mainMedia, setMainMedia] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Autres outils
  const [autresOutils, setAutresOutils] = useState([]);

  const videoRef = useRef(null);

  // 🔹 Récupération de l'outil + médias
  useEffect(() => {
    const fetchOutil = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, "items"), where("slug", "==", slug));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
          setOutil(null);
          return;
        }

        const docSnap = snapshot.docs[0];
        const outilData = { id: docSnap.id, ...docSnap.data() };
        setOutil(outilData);

        // Construction de la liste des médias
        const medias = [];
        const imagesOnly = [];

        if (outilData.videoUrls) {
          if (Array.isArray(outilData.videoUrls)) {
            outilData.videoUrls.forEach((vUrl) => {
              medias.push({ type: "video", src: vUrl });
            });
          } else {
            medias.push({ type: "video", src: outilData.videoUrls });
          }
        }

        if (outilData.imageUrls?.length) {
          outilData.imageUrls.forEach((url) => {
            const imgMedia = { type: "image", src: url };
            medias.push(imgMedia);
            imagesOnly.push(imgMedia);
          });
        }

        setMediaList(medias);
        setImageList(imagesOnly);

        if (medias.length > 0) {
          setMainMedia(medias[0]);
          setCurrentMediaIndex(0);
        }

        // Suggestions : même marque
        if (outilData.marque) {
          const suggestions = await getDocs(
            query(collection(db, "items"), where("marque", "==", outilData.marque))
          );
          const autres = suggestions.docs
            .filter((d) => d.id !== docSnap.id)
            .map((d) => ({ id: d.id, ...d.data() }));
          setAutresOutils(autres);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération de l'outil:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOutil();
  }, [slug]);

  // 🔹 Autoplay vidéo quand on change de média
  useEffect(() => {
    if (mainMedia?.type === "video" && videoRef.current) {
      videoRef.current
        .play()
        .catch((err) => console.log("Autoplay bloqué:", err));
    }
  }, [mainMedia]);

  // 🔹 Navigation médias
  const goToPrev = () => {
    if (!mediaList.length) return;
    const newIndex =
      (currentMediaIndex - 1 + mediaList.length) % mediaList.length;
    setCurrentMediaIndex(newIndex);
    setMainMedia(mediaList[newIndex]);
  };

  const goToNext = () => {
    if (!mediaList.length) return;
    const newIndex = (currentMediaIndex + 1) % mediaList.length;
    setCurrentMediaIndex(newIndex);
    setMainMedia(mediaList[newIndex]);
  };

  // 🔹 Lightbox sur une image
  const openLightboxFromMedia = (media) => {
    if (!media || media.type !== "image") return;
    const indexInImages = imageList.findIndex((m) => m.src === media.src);
    if (indexInImages >= 0) {
      setLightboxIndex(indexInImages);
      setLightboxOpen(true);
    }
  };

  /* -------------------------
     États intermédiaires
  --------------------------*/

  if (loading) {
    return (
      <>
        <MenuNav />
        <MobileMenu />
        <div className="container my-5">
          <div className="row g-4">
            <div className="col-12 col-lg-6">
              <div className="placeholder-glow">
                <div
                  className="placeholder col-12"
                  style={{ height: "320px", borderRadius: "0.75rem" }}
                />
              </div>
              <div className="d-flex gap-2 mt-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="placeholder-glow"
                    style={{ width: "70px", height: "70px" }}
                  >
                    <span className="placeholder col-12 h-100 rounded" />
                  </div>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <div className="placeholder-glow mb-2">
                <span className="placeholder col-8" />
              </div>
              <div className="placeholder-glow mb-2">
                <span className="placeholder col-4" />
              </div>
              <div className="placeholder-glow mb-3">
                <span className="placeholder col-6" />
              </div>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="placeholder-glow mb-2">
                  <span className="placeholder col-12" />
                </div>
              ))}
            </div>
          </div>
        </div>
        <FooterOutils />
      </>
    );
  }

  if (!outil) {
    return (
      <>
        <MenuNav />
        <MobileMenu />
        <div className="text-center mt-5 text-danger">Outil introuvable.</div>
        <FooterOutils />
      </>
    );
  }

  /* -------------------------
     Ici seulement : outil est garanti NON null
  --------------------------*/

  const fiche = outil.ficheTechnique || {};

  const whatsappLink = `https://wa.me/22800000000?text=Bonjour%2C%20je%20suis%20int%C3%A9ress%C3%A9(e)%20par%20l'outil%20"${encodeURIComponent(
    outil.nom || ""
  )}"%20(%20${encodeURIComponent(window.location.href)}%20)`;

  const seoDescription = outil.description
    ? outil.description.slice(0, 155)
    : `Détail et caractéristiques de l’outil agricole ${outil.nom || ""} proposé par LA F.A.P.E.`;

  return (
    <>
      <MenuNav />
      <MobileMenu />

      {/* ✅ SEO dynamique pour la fiche outil */}
      <Helmet>
        <title>
          {outil.nom
            ? `${outil.nom} | Outil agricole – LA F.A.P.E`
            : "Détail outil agricole – LA F.A.P.E"}
        </title>

        <meta name="description" content={seoDescription} />

        <meta
          property="og:title"
          content={`${outil.nom || "Outil agricole"} | LA F.A.P.E`}
        />
        <meta property="og:description" content={seoDescription} />
        <meta
          property="og:url"
          content={`https://ton-domaine.com/outil/${outil.slug}`}
        />
        {outil.imageUrls?.[0] && (
          <meta property="og:image" content={outil.imageUrls[0]} />
        )}
        <meta property="og:type" content="product" />

        {/* Rich snippet produit */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: outil.nom,
            brand: outil.marque,
            description:
              outil.description || "Outil agricole proposé par LA F.A.P.E.",
            image: outil.imageUrls || [],
            offers: {
              "@type": "Offer",
              priceCurrency: "XOF",
              price: outil.prix || "0",
              availability: "https://schema.org/InStock",
            },
          })}
        </script>
      </Helmet>

      <div className="container my-4">
        {/* Fil d'Ariane simple */}
        <div className="mb-3 small">
          <Link
            to="/catalogue"
            className="text-decoration-none text-muted"
          >
            ← Retour au catalogue
          </Link>
        </div>

        <div className="row g-4">
          {/* Colonne Médias */}
          <div className="col-12 col-lg-6 position-relative">
            {mainMedia ? (
              <>
                <div className="position-relative">
                  {mainMedia.type === "image" ? (
                    <img
                      src={mainMedia.src}
                      className="img-fluid rounded shadow-sm cursor-pointer w-100"
                      alt={outil.nom}
                      onClick={() => openLightboxFromMedia(mainMedia)}
                      style={{ objectFit: "contain", maxHeight: "420px" }}
                    />
                  ) : (
                    <div className="position-relative">
                      <video
                        key={mainMedia.src}
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        controls
                        className="w-100 rounded shadow-sm"
                        poster={outil.imageUrls?.[0]}
                        style={{ maxHeight: "420px", objectFit: "contain" }}
                      >
                        <source src={mainMedia.src} type="video/mp4" />
                      </video>
                      <span className="badge bg-dark position-absolute top-0 start-0 m-2">
                        🎥 Vidéo de démonstration
                      </span>
                    </div>
                  )}

                  {mediaList.length > 1 && (
                    <>
                      <button
                        className="btn btn-light rounded-circle position-absolute top-50 start-0 translate-middle-y shadow-sm"
                        onClick={goToPrev}
                        aria-label="Précédent"
                      >
                        ←
                      </button>
                      <button
                        className="btn btn-light rounded-circle position-absolute top-50 end-0 translate-middle-y shadow-sm"
                        onClick={goToNext}
                        aria-label="Suivant"
                      >
                        →
                      </button>
                    </>
                  )}
                </div>

                {mediaList.length > 1 && (
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {mediaList.map((media, i) =>
                      media.type === "image" ? (
                        <img
                          key={i}
                          src={media.src}
                          alt={`img-${i}`}
                          className={`img-thumbnail cursor-pointer ${
                            mainMedia?.src === media.src
                              ? "border-success border-3"
                              : ""
                          }`}
                          style={{
                            width: "70px",
                            height: "70px",
                            objectFit: "cover",
                          }}
                          onClick={() => {
                            setMainMedia(media);
                            setCurrentMediaIndex(i);
                          }}
                        />
                      ) : (
                        <div
                          key={i}
                          className={`bg-dark text-white d-flex flex-column justify-content-center align-items-center cursor-pointer rounded ${
                            mainMedia?.src === media.src
                              ? "border border-success border-3"
                              : ""
                          }`}
                          style={{
                            width: "70px",
                            height: "70px",
                            fontSize: "0.8rem",
                          }}
                          onClick={() => {
                            setMainMedia(media);
                            setCurrentMediaIndex(i);
                          }}
                          title="Lire la vidéo"
                        >
                          🎥
                          <span className="small">Vidéo</span>
                        </div>
                      )
                    )}
                  </div>
                )}
              </>
            ) : (
              <div
                className="border rounded d-flex align-items-center justify-content-center"
                style={{ height: "320px" }}
              >
                <span className="text-muted">Aucun média disponible</span>
              </div>
            )}
          </div>

          {/* Colonne Infos produit */}
          <div className="col-12 col-lg-6">
            <h1 className="mb-1 h3">{outil.nom}</h1>
            <p className="text-muted mb-2">
              {outil.marque && <span>{outil.marque}</span>}
              {outil.marque && outil.modele && " • "}
              {outil.modele && <span>{outil.modele}</span>}
            </p>

            <p className="mb-2">
              <strong>Prix :</strong>{" "}
              <span className="text-success fs-5">
                {outil.prix
                  ? `${outil.prix.toLocaleString()} FCFA`
                  : "Prix à la demande"}
              </span>
            </p>

            <p className="mb-1">
              <strong>Stock :</strong>{" "}
              <span>{outil.stock ? outil.stock : "À confirmer"}</span>
            </p>
            <p className="mb-1">
              <strong>État :</strong>{" "}
              <span>{outil.etat || "Non précisé"}</span>
            </p>
            <p className="mb-3">
              <strong>Lieu :</strong>{" "}
              <span>{outil.location || "Non précisé"}</span>
            </p>

            {outil.description && (
              <div className="mb-3">
                <h5 className="mb-1">Description</h5>
                <p
                  className="text-muted mb-0"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {outil.description}
                </p>
              </div>
            )}

            <div className="mb-3">
              <h5 className="mb-2">Fiche technique</h5>
              <ul className="list-group list-group-flush small">
                <li className="list-group-item">
                  <strong>Poids :</strong> {fiche.poids || "N/A"}
                </li>
                <li className="list-group-item">
                  <strong>Puissance :</strong> {fiche.puissance || "N/A"}
                </li>
                <li className="list-group-item">
                  <strong>Dimensions :</strong> {fiche.dimensions || "N/A"}
                </li>
                <li className="list-group-item">
                  <strong>Capacité :</strong> {fiche.capacite || "N/A"}
                </li>
                <li className="list-group-item">
                  <strong>Utilisation :</strong> {fiche.utilisation || "N/A"}
                </li>
              </ul>
            </div>

            <div className="d-flex flex-wrap gap-2 mt-3">
              <a
                href={whatsappLink}
                className="btn btn-bridge"
                target="_blank"
                rel="noreferrer"
              >
                📲 Contacter pour achat
              </a>
              <Link to="/catalogue" className="btn btn-outline-success">
                ← Retour au catalogue
              </Link>
            </div>
          </div>
        </div>

        {/* Autres outils de la marque */}
        {autresOutils.length > 0 && (
          <div className="mt-5">
            <h4 className="mb-3">
              🔧 Autres outils de la marque {outil.marque}
            </h4>
            <div className="row">
              {autresOutils.map((item) => (
                <div key={item.id} className="col-6 col-md-4 col-lg-3 mb-3">
                  <Link
                    to={`/outil/${item.slug}`}
                    className="text-decoration-none"
                  >
                    <div className="card h-100 hover-elevate">
                      {item.imageUrls?.[0] && (
                        <div className="ratio ratio-4x3">
                          <img
                            src={item.imageUrls[0]}
                            className="card-img-top"
                            alt={item.nom}
                            style={{ objectFit: "cover" }}
                          />
                        </div>
                      )}
                      <div className="card-body p-2">
                        <h6 className="card-title mb-1 text-dark text-truncate">
                          {item.nom}
                        </h6>
                        <p className="small text-muted mb-1 text-truncate">
                          {item.modele}
                        </p>
                        <p className="fw-bold text-success small mb-0">
                          {item.prix
                            ? `${item.prix.toLocaleString()} FCFA`
                            : "Prix à la demande"}
                        </p>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Lightbox pour les images */}
      {lightboxOpen && imageList.length > 0 && (
        <Lightbox
          open={lightboxOpen}
          close={() => setLightboxOpen(false)}
          index={lightboxIndex}
          slides={imageList.map((img) => ({ src: img.src }))}
        />
      )}

      <FooterOutils />
    </>
  );
}
